/**
 * Sonner Toast Hook
 * 
 * A simplified hook that uses Sonner's native variants with richColors.
 * Automatically includes custom close button.
 */

import React from "react"  
import { toast as sonnerToast } from "sonner"
import { CustomCloseButton } from "../components/ui/sonner"

interface ToastOptions {
  title?: string
  description?: string
  variant?: "default" | "destructive" | "success" | "warning" | "info"
  action?: React.ReactNode
}

// Helper function to create toast content with custom close button
const createToastContent = (
  title: string | undefined,
  description: string | undefined,
  action: React.ReactNode | undefined,
  toastId: string | number
) => {
  return React.createElement(
    "div",
    { className: "flex items-start justify-between w-full" },
    React.createElement(
      "div",
      { className: "flex-1" },
      title && React.createElement("div", { className: "font-medium" }, title),
      description && React.createElement("div", { className: "text-sm opacity-90" }, description),
      action && React.createElement("div", { className: "mt-2" }, action)
    ),
    React.createElement(CustomCloseButton, { toastId })
  )
}

export function toast({ title, description, variant = "default", action }: ToastOptions) {
  // Generate unique ID for this toast
  const toastId = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

  const content = createToastContent(title, description, action, toastId)

  // Use Sonner's native methods with richColors handling styling automatically
  switch (variant) {
    case "destructive":
      sonnerToast.error(content, { id: toastId })
      break
    case "success":
      sonnerToast.success(content, { id: toastId })
      break
    case "warning":
      sonnerToast.warning(content, { id: toastId })
      break
    case "info":
      sonnerToast.info(content, { id: toastId })
      break
    default:
      sonnerToast(content, { id: toastId })
      break
  }

  return {
    id: toastId,
    dismiss: () => sonnerToast.dismiss(toastId),
  }
}

// Convenience functions for each variant
export const toastSuccess = (title: string, description?: string, action?: React.ReactNode) =>
  toast({ title, description, variant: "success", action })

export const toastError = (title: string, description?: string, action?: React.ReactNode) =>
  toast({ title, description, variant: "destructive", action })

export const toastWarning = (title: string, description?: string, action?: React.ReactNode) =>
  toast({ title, description, variant: "warning", action })

export const toastInfo = (title: string, description?: string, action?: React.ReactNode) =>
  toast({ title, description, variant: "info", action })

// Additional utility functions
export const dismissToast = (toastId?: string | number) => sonnerToast.dismiss(toastId)
export const dismissAllToasts = () => sonnerToast.dismiss()

// Export the original sonner toast as well for advanced usage
export { toast as sonnerToast } from "sonner"